import { sma as pineSma } from '../core/indicators'

export type OhlcCandle = {
    open: number
    high: number
    low: number
    close: number
    index: number
}

export const DOJI_BODY_RATIO = 0.1
export const SMALL_BODY_RATIO = 0.35
export const LONG_BODY_RATIO = 0.55
export const SHADOW_TO_BODY_RATIO = 2

/** TradingView built-in candlestick pattern constants */
export const TV_BODY_EMA_LEN = 14
export const TV_SHADOW_PERCENT = 5
export const TV_DOJI_BODY_PERCENT = 5
export const TV_SHADOW_FACTOR = 2
export const TV_BODY_AVG_TOLERANCE = 0.05

function priceEpsilon(price: number): number {
    return Math.max(Math.abs(price) * 1e-10, 1e-6)
}

export function candleBody(c: OhlcCandle): number {
    return Math.abs(c.close - c.open)
}

export function candleRange(c: OhlcCandle): number {
    return c.high - c.low
}

export function upperShadow(c: OhlcCandle): number {
    return c.high - Math.max(c.open, c.close)
}

export function lowerShadow(c: OhlcCandle): number {
    return Math.min(c.open, c.close) - c.low
}

export function isBullish(c: OhlcCandle): boolean {
    return c.close > c.open
}

export function isBearish(c: OhlcCandle): boolean {
    return c.close < c.open
}

export function bodyTop(c: OhlcCandle): number {
    return Math.max(c.open, c.close)
}

export function bodyBottom(c: OhlcCandle): number {
    return Math.min(c.open, c.close)
}

export function isDoji(c: OhlcCandle, ratio = DOJI_BODY_RATIO): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false
    return candleBody(c) / range <= ratio
}

export function isSmallBody(c: OhlcCandle, ratio = SMALL_BODY_RATIO): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false
    return candleBody(c) / range <= ratio
}

export function isLongBody(c: OhlcCandle, ratio = LONG_BODY_RATIO): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false
    return candleBody(c) / range >= ratio
}

export function hasLongLowerShadow(c: OhlcCandle): boolean {
    const body = candleBody(c)
    if (body <= 0) return lowerShadow(c) > 0
    return lowerShadow(c) >= body * SHADOW_TO_BODY_RATIO
}

export function hasLongUpperShadow(c: OhlcCandle): boolean {
    const body = candleBody(c)
    if (body <= 0) return upperShadow(c) > 0
    return upperShadow(c) >= body * SHADOW_TO_BODY_RATIO
}

export function hasTinyUpperShadow(c: OhlcCandle): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return true
    return upperShadow(c) / range <= 0.1
}

export function hasTinyLowerShadow(c: OhlcCandle): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return true
    return lowerShadow(c) / range <= 0.1
}

/** Pine v5 `ta.ema` — first value seeds from bar 0 (TradingView built-in candlestick patterns) */
export function computePineTaEma(source: number[], period: number): number[] {
    const result = new Array(source.length).fill(NaN)
    const alpha = 2 / (period + 1)
    let prev: number | null = null
    for (let i = 0; i < source.length; i++) {
        const v = source[i]
        if (!Number.isFinite(v)) continue
        prev = prev == null ? v : alpha * v + (1 - alpha) * prev
        result[i] = prev
    }
    return result
}

/** EMA of candle body sizes — matches Pine v5 `ta.ema(C_Body, 14)` used by TV built-in patterns */
export function computeBodyEma(open: number[], close: number[], period = TV_BODY_EMA_LEN): number[] {
    const bodies = close.map((c, i) => Math.abs(c - open[i]))
    return computePineTaEma(bodies, period)
}

export function computeTrendSmaSeries(close: number[]): { sma50: number[]; sma200: number[] } {
    return {
        sma50: pineSma(close, 50),
        sma200: pineSma(close, 200),
    }
}

export function hasTradingViewUpShadow(c: OhlcCandle, eps = 0): boolean {
    const body = candleBody(c)
    if (body <= 0) return upperShadow(c) > eps
    return upperShadow(c) > (TV_SHADOW_PERCENT / 100) * body + eps
}

export function hasTradingViewDownShadow(c: OhlcCandle, eps = 0): boolean {
    const body = candleBody(c)
    if (body <= 0) return lowerShadow(c) > eps
    return lowerShadow(c) > (TV_SHADOW_PERCENT / 100) * body + eps
}

export function bodyMiddle(c: OhlcCandle): number {
    return (bodyTop(c) + bodyBottom(c)) / 2
}

export function isTradingViewLongBody(c: OhlcCandle, bodyEma: number): boolean {
    const body = candleBody(c)
    return Number.isFinite(bodyEma) && body > bodyEma
}

export function isTradingViewSmallBody(c: OhlcCandle, bodyEma: number): boolean {
    const body = candleBody(c)
    return Number.isFinite(bodyEma) && body < bodyEma
}

export function isTradingViewDojiBody(c: OhlcCandle): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false
    return candleBody(c) <= range * (TV_DOJI_BODY_PERCENT / 100)
}

/** Three White Soldiers: upper shadow smaller than 5% of candle range */
export function haveNotUpShadow(c: OhlcCandle): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false
    return range * (TV_SHADOW_PERCENT / 100) > upperShadow(c)
}

export function pricesWithinBodyAvgTolerance(a: number, b: number, bodyAvg: number): boolean {
    if (!Number.isFinite(bodyAvg)) return false
    return Math.abs(a - b) <= bodyAvg * TV_BODY_AVG_TOLERANCE
}

/** TradingView "Hammer - Bull" shape: small body above hl2, long lower shadow, no upper shadow */
export function isTradingViewHammer(c: OhlcCandle, bodyEma: number): boolean {
    const body = candleBody(c)
    if (body <= 0 || !Number.isFinite(bodyEma)) return false
    if (body >= bodyEma) return false

    const eps = priceEpsilon(c.close)
    const hl2 = (c.high + c.low) / 2
    if (bodyBottom(c) <= hl2 + eps) return false
    if (lowerShadow(c) + eps < TV_SHADOW_FACTOR * body) return false
    if (hasTradingViewUpShadow(c, eps)) return false
    return true
}

/** TradingView "Inverted Hammer - Bull" shape: small body below hl2, long upper shadow, no lower shadow */
export function isTradingViewInvertedHammer(c: OhlcCandle, bodyEma: number): boolean {
    const body = candleBody(c)
    if (body <= 0 || !Number.isFinite(bodyEma)) return false
    if (body >= bodyEma) return false

    const eps = priceEpsilon(c.close)
    const hl2 = (c.high + c.low) / 2
    if (bodyTop(c) >= hl2 - eps) return false
    if (upperShadow(c) + eps < TV_SHADOW_FACTOR * body) return false
    if (hasTradingViewDownShadow(c, eps)) return false
    return true
}

/** Hammer / inverted-hammer style: long lower wick, small upper wick, compact body */
export function isHammerShaped(c: OhlcCandle): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false

    const body = candleBody(c)
    const lower = lowerShadow(c)
    const upper = upperShadow(c)
    const bodyBottomPrice = bodyBottom(c)

    // Real body should sit in the upper half of the candle range
    if ((bodyBottomPrice - c.low) / range < 0.55) return false

    if (body <= 0) {
        return lower / range >= 0.55 && upper / range <= 0.12
    }

    return lower >= body * 2 && upper <= body && body / range <= 0.4
}

/** Shooting star / hanging-man style: long upper wick, small lower wick */
export function isShootingStarShaped(c: OhlcCandle): boolean {
    const range = candleRange(c)
    if (!Number.isFinite(range) || range <= 0) return false

    const body = candleBody(c)
    const lower = lowerShadow(c)
    const upper = upperShadow(c)
    const bodyTopPrice = bodyTop(c)

    if ((c.high - bodyTopPrice) / range < 0.55) return false

    if (body <= 0) {
        return upper / range >= 0.55 && lower / range <= 0.12
    }

    return upper >= body * 2 && lower <= body && body / range <= 0.4
}

/** Pattern appears near a recent swing low (bullish reversal context) */
export function isNearSwingLow(low: number[], idx: number, lookback = 12): boolean {
    const start = Math.max(0, idx - lookback + 1)
    let minLow = Infinity
    for (let i = start; i <= idx; i++) {
        if (Number.isFinite(low[i])) minLow = Math.min(minLow, low[i])
    }
    if (!Number.isFinite(minLow)) return false
    return low[idx] <= minLow * 1.003
}

/** Pattern appears near a recent swing high (bearish reversal context) */
export function isNearSwingHigh(high: number[], idx: number, lookback = 12): boolean {
    const start = Math.max(0, idx - lookback + 1)
    let maxHigh = -Infinity
    for (let i = start; i <= idx; i++) {
        if (Number.isFinite(high[i])) maxHigh = Math.max(maxHigh, high[i])
    }
    if (!Number.isFinite(maxHigh)) return false
    return high[idx] >= maxHigh * 0.997
}

export function pricesNear(a: number, b: number, tolerancePct = 0.15): boolean {
    const avg = (a + b) / 2
    if (!Number.isFinite(avg) || avg === 0) return false
    return (Math.abs(a - b) / avg) * 100 <= tolerancePct
}

export function toOhlc(
    open: number[],
    high: number[],
    low: number[],
    close: number[],
    index: number
): OhlcCandle | null {
    if (index < 0 || index >= close.length) return null
    const o = open[index]
    const h = high[index]
    const l = low[index]
    const c = close[index]
    if (![o, h, l, c].every(Number.isFinite)) return null
    return { open: o, high: h, low: l, close: c, index }
}

export function recentDowntrend(close: number[], endIdx: number, lookback = 5): boolean {
    const start = Math.max(0, endIdx - lookback)
    if (endIdx - start < 2) return true
    return close[endIdx] < close[start]
}

export function recentUptrend(close: number[], endIdx: number, lookback = 5): boolean {
    const start = Math.max(0, endIdx - lookback)
    if (endIdx - start < 2) return true
    return close[endIdx] > close[start]
}

type TrendSmaSeries = {
    sma50?: number[]
    sma200?: number[]
}

/** TradingView-style bullish trend filter for hammer patterns */
export function passesBullishTrendFilter(
    close: number[],
    idx: number,
    trendBasedOn?: string,
    precomputed?: TrendSmaSeries,
): boolean {
    const mode = trendBasedOn ?? 'SMA50'
    if (mode === 'No detection') return true

    const price = close[idx]
    if (!Number.isFinite(price)) return false

    if (mode === 'SMA50') {
        const sma50Series = precomputed?.sma50 ?? pineSma(close, 50)
        const sma50 = sma50Series[idx]
        if (!Number.isFinite(sma50)) return false
        return price < sma50
    }

    if (mode === 'SMA50, SMA200') {
        const sma50Series = precomputed?.sma50 ?? pineSma(close, 50)
        const sma200Series = precomputed?.sma200 ?? pineSma(close, 200)
        const sma50 = sma50Series[idx]
        const sma200 = sma200Series[idx]
        if (!Number.isFinite(sma50) || !Number.isFinite(sma200)) return false
        return price < sma50 && sma50 < sma200
    }

    return true
}

/** TradingView-style bearish trend filter for candlestick reversal patterns */
export function passesBearishTrendFilter(
    close: number[],
    idx: number,
    trendBasedOn?: string,
    precomputed?: TrendSmaSeries,
): boolean {
    const mode = trendBasedOn ?? 'SMA50'
    if (mode === 'No detection') return true

    const price = close[idx]
    if (!Number.isFinite(price)) return false

    if (mode === 'SMA50') {
        const sma50Series = precomputed?.sma50 ?? pineSma(close, 50)
        const sma50 = sma50Series[idx]
        if (!Number.isFinite(sma50)) return false
        return price > sma50
    }

    if (mode === 'SMA50, SMA200') {
        const sma50Series = precomputed?.sma50 ?? pineSma(close, 50)
        const sma200Series = precomputed?.sma200 ?? pineSma(close, 200)
        const sma50 = sma50Series[idx]
        const sma200 = sma200Series[idx]
        if (!Number.isFinite(sma50) || !Number.isFinite(sma200)) return false
        return price > sma50 && sma50 > sma200
    }

    return true
}

export function hasGapDown(prev: OhlcCandle, curr: OhlcCandle): boolean {
    return curr.high < bodyBottom(prev)
}

export function hasGapUp(prev: OhlcCandle, curr: OhlcCandle): boolean {
    return curr.low > bodyTop(prev)
}
